import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import WebDevelopmentIcon from '../components/icons/WebDevelopmentIcon'
import CloudIcon from '../components/icons/CloudIcon'
import ConsultingIcon from '../components/icons/ConsultingIcon'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'
import { ICONS } from '../components/icons/registry'

interface Service {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  features: string[]
}

interface Industry {
  name: string
  description: string
  icon: React.ComponentType<{ className?: string; darkTreatment?: 'silhouette' | 'none' }>
}

const DubaiServices = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services: Service[] = [
    {
      id: 'software-development',
      title: 'Software Development',
      description: 'Custom software solutions designed to address the unique challenges of businesses operating in Dubai and the wider UAE market. We build enterprise-grade applications that scale with your growth.',
      icon: WebDevelopmentIcon,
      features: [
        'Enterprise Resource Planning (ERP) Systems',
        'Customer Relationship Management (CRM) Platforms',
        'Business Process Automation',
        'Custom API Development & Integration',
        'Legacy System Modernization',
        'Real-time Data Processing Solutions'
      ]
    },
    {
      id: 'web-development',
      title: 'Web Development',
      description: 'Professional web development services tailored for the Dubai market. We create fast, secure, and visually compelling websites that convert visitors into customers and strengthen your digital presence.',
      icon: WebDevelopmentIcon,
      features: [
        'Responsive Corporate Websites',
        'E-commerce Platforms with Payment Gateway Integration',
        'Progressive Web Applications (PWA)',
        'Content Management Systems',
        'Multi-language Support (Arabic & English)',
        'Performance Optimization & SEO'
      ]
    },
    {
      id: 'mobile-development',
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications built for the UAE market. We deliver intuitive, high-performance apps that engage users and drive business results across iOS and Android.',
      icon: CloudIcon,
      features: [
        'iOS & Android Native Development',
        'Cross-platform Solutions (React Native, Flutter)',
        'Enterprise Mobile Applications',
        'E-commerce & Marketplace Apps',
        'Mobile Payment Integration',
        'App Store Optimization (ASO)'
      ]
    },
    {
      id: 'it-consulting',
      title: 'IT Consulting',
      description: 'Strategic technology consulting to help Dubai businesses navigate digital transformation. We align your IT infrastructure with business objectives and UAE regulatory requirements.',
      icon: ConsultingIcon,
      features: [
        'Digital Transformation Strategy',
        'IT Infrastructure Assessment',
        'Cloud Migration Planning',
        'Cybersecurity Consulting',
        'Technology Roadmap Development',
        'Vendor Selection & Management'
      ]
    }
  ]

  const industries: Industry[] = [
    {
      name: 'Finance & Banking',
      description: 'Secure, compliant fintech solutions for Dubai International Financial Centre (DIFC) and UAE Central Bank regulated entities.',
      icon: ICONS.finance
    },
    {
      name: 'Real Estate',
      description: 'Property management platforms, virtual tours, and CRM solutions for Dubai\'s thriving real estate market.',
      icon: ICONS.realEstate
    },
    {
      name: 'Healthcare',
      description: 'HIPAA-compliant healthcare applications, telemedicine platforms, and patient management systems for UAE medical facilities.',
      icon: ICONS.healthcare
    },
    {
      name: 'E-commerce & Retail',
      description: 'Omnichannel retail solutions with Arabic language support, local payment gateways, and logistics integration.',
      icon: ICONS.ecommerce
    },
    {
      name: 'Government & Public Sector',
      description: 'Digital government services aligned with UAE\'s Smart Government initiative and Dubai\'s paperless strategy.',
      icon: ICONS.government
    }
  ]

  const advantages = [
    {
      title: 'UAE Market Expertise',
      description: 'Deep understanding of local business practices, regulatory requirements, and cultural nuances specific to the Dubai and UAE market.'
    },
    {
      title: 'Timezone Alignment',
      description: 'Our team operates within Gulf Standard Time (GST), ensuring real-time collaboration and rapid response during your business hours.'
    },
    {
      title: 'Multilingual Support',
      description: 'Full Arabic and English language support for both project communication and end-user interfaces, including RTL design implementation.'
    },
    {
      title: 'Local Payment Integration',
      description: 'Expert integration with UAE payment gateways including Network International, Telr, PayTabs, and Apple Pay Middle East.'
    },
    {
      title: 'Regulatory Compliance',
      description: 'Solutions built to comply with UAE data protection laws, DIFC regulations, and industry-specific requirements.'
    },
    {
      title: 'Proven Track Record',
      description: 'Successful delivery of projects for businesses across Dubai, Abu Dhabi, Sharjah, and the wider GCC region.'
    }
  ]

  const blogPosts = [
    {
      title: 'Top Software Development Trends in Dubai for 2026',
      description: 'Discover the leading software development trends shaping Dubai\'s tech landscape, from AI integration to cloud-native solutions.',
      path: '/blog/dubai-software-trends-2026',
      icon: ICONS.launch
    },
    {
      title: 'How to Choose the Right Web Development Agency in Dubai',
      description: 'A comprehensive guide to selecting the best web development partner for your business in the UAE.',
      path: '/blog/web-development-agency-dubai',
      icon: ICONS.webApplications
    },
    {
      title: 'Mobile App Development Cost in Dubai: Complete Guide 2026',
      description: 'Comprehensive guide to mobile app development costs in Dubai and UAE, including pricing factors and budgeting tips.',
      path: '/blog/mobile-app-development-cost-dubai',
      icon: ICONS.mobile
    },
    {
      title: 'Digital Transformation in UAE: A Business Guide for 2026',
      description: 'Learn about cloud adoption, AI automation, and how IT consulting partners help businesses navigate their digital journey.',
      path: '/blog/digital-transformation-uae',
      icon: ICONS.process
    }
  ]

  const specialistServices = [
    { title: 'DevOps Services', path: '/devops-services-dubai', description: 'CI/CD pipelines, infrastructure automation, and cloud migration for Dubai businesses.' },
    { title: 'Kubernetes Consulting', path: '/kubernetes-consulting-dubai', description: 'Container orchestration and microservices architecture for scalable applications.' },
    { title: 'AWS Cloud Consulting', path: '/aws-cloud-consulting-dubai', description: 'Cloud architecture, migration, and cost optimization on Amazon Web Services.' },
    { title: 'DevSecOps', path: '/devsecops-dubai', description: 'Security-integrated development pipelines and compliance automation.' },
    { title: 'E-commerce Development', path: '/ecommerce-development-dubai', description: 'Custom online stores with UAE payment gateways and Arabic language support.' },
    { title: 'Managed IT Services', path: '/managed-it-services-dubai', description: '24/7 infrastructure monitoring, maintenance, and technical support.' },
  ]

  const faqs = [
    {
      question: 'What web development services does Demirix offer in Dubai?',
      answer: 'Demirix is a full-service web development agency in Dubai offering custom website design, responsive corporate websites, e-commerce platforms with UAE payment gateway integration, progressive web applications, content management systems, and multi-language support including Arabic RTL design. We serve businesses across Dubai, Abu Dhabi, and the wider UAE market.'
    },
    {
      question: 'How much does website design cost in Dubai?',
      answer: 'Website design costs in Dubai vary based on complexity. A professional corporate website starts from AED 15,000 to AED 50,000. E-commerce platforms with payment gateway integration range from AED 30,000 to AED 150,000. Custom web applications with advanced functionality can range from AED 75,000 to AED 350,000. We provide detailed proposals with fixed milestones so you know costs upfront.'
    },
    {
      question: 'How long does it take to build a website in Dubai?',
      answer: 'A standard corporate website takes 4 to 6 weeks to design and develop. E-commerce platforms typically require 8 to 12 weeks depending on the number of products, payment integrations, and custom features. Complex web applications with custom functionality can take 12 to 20 weeks. We use agile development processes so you see progress from the first sprint.'
    },
    {
      question: 'Why should I choose a web development company in Dubai over offshore developers?',
      answer: 'Choosing a Dubai-based web development company like Demirix gives you timezone alignment for real-time collaboration, understanding of UAE business culture and regulatory requirements, expertise with local payment gateways like Network International and Telr, Arabic and English bilingual support, and compliance with UAE data protection laws. Offshore developers often lack this local market knowledge which leads to costly revisions.'
    },
    {
      question: 'Do you provide ongoing website maintenance and support?',
      answer: 'Yes, we offer flexible website maintenance and support packages starting from AED 2,000 per month. This includes security updates, performance monitoring, content updates, bug fixes, uptime monitoring, and monthly performance reports. We also offer managed IT services for businesses that need comprehensive technical support beyond just their website.'
    },
    {
      question: 'Can you help with SEO and digital marketing for my Dubai website?',
      answer: 'Yes, all our websites are built with SEO best practices from the ground up, including optimized site structure, fast loading speeds, mobile responsiveness, structured data markup, and keyword-optimized content. We also offer ongoing SEO services including keyword research, content strategy, technical SEO audits, and local SEO optimization to improve your organic traffic and search engine rankings in the UAE market.'
    },
    {
      question: 'What technologies do you use for web development?',
      answer: 'We use modern, industry-leading technologies including React, Next.js, and Vue.js for frontend development, Node.js, Python, and Go for backend services, AWS, Azure, and GCP for cloud infrastructure, Docker and Kubernetes for containerization, and Terraform for infrastructure as code. We select the best technology stack based on your project requirements, scalability needs, and long-term maintenance considerations.'
    },
    {
      question: 'Do you work with startups and enterprise businesses in Dubai?',
      answer: 'Yes, we work with businesses of all sizes in Dubai and the UAE. For startups, we offer MVP development, technical consulting, and scalable architecture design. For enterprises, we provide custom software development, system integration, legacy modernization, and ongoing managed IT services. Our team has experience across DIFC, DMCC, and other Dubai free zones.'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)

  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'Dubai', path: '/dubai-services' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Web Development Agency Dubai | Website Design Dubai & IT Consulting UAE"
        description="Leading software development company in Dubai offering web development, mobile app development, and IT consulting services. Transform your business with our expert technology solutions tailored for the UAE market."
        keywords={[
          'software development company dubai',
          'web development company in dubai',
          'web development dubai',
          'it consulting dubai',
          'mobile app development dubai',
          'software company dubai',
          'web design dubai',
          'it services dubai uae',
          'custom software dubai',
          'enterprise software dubai',
          'digital transformation dubai',
          'technology consulting uae'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              Dubai, UAE
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Web Development Agency & Website Design in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              As a leading web design company in Dubai, we deliver high quality website development, custom web applications, and IT consulting services
              that strengthen your digital presence and drive conversion rates. Our designers and developers
              build user-friendly solutions for businesses across the UAE, from startups in DIFC to enterprises
              seeking long-term digital market growth and improved online presence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto"
              >
                Schedule a Consultation
              </CalendlyButton>
              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Explore All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Services in Dubai
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Comprehensive web development services and technology solutions tailored for the Dubai market.
                Our experienced designers and developers deliver user experience excellence and brand identity
                that drives organic traffic and business growth across the UAE.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <Icon className="w-14 h-14 flex-shrink-0" />
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Why Choose Demirix for Your Dubai Projects
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We combine global technology expertise with deep local knowledge to deliver solutions
                that drive results in the UAE market.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {advantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600"
                >
                  <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {advantage.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Industries We Serve in the UAE
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Specialized solutions for key sectors driving Dubai's economy, built with industry-specific
                expertise and compliance requirements in mind.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="group relative bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-12 h-12 mb-4" aria-hidden="true">
                    <industry.icon className="w-full h-full" darkTreatment="none" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {industry.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {industry.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { number: '100%', label: 'Client Satisfaction' },
              { number: '24/7', label: 'Support Availability' },
              { number: 'GST', label: 'Timezone Aligned' },
              { number: 'Agile', label: 'Development Process' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-primary-100 text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Specialist Services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Specialist Technology Services in Dubai
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Beyond custom web development, we offer deep expertise in cloud infrastructure,
                DevOps automation, and enterprise-grade e-commerce development for the UAE market.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specialistServices.map((service, index) => (
                <motion.div key={index} variants={itemVariants}>
                  <Link
                    to={service.path}
                    className="group block bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-500 hover:shadow-lg transition-all duration-300 h-full"
                  >
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium group-hover:underline">
                      Learn more
                      <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about web development and IT services in Dubai.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 border border-gray-100 dark:border-gray-600"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Abu Dhabi Services Cross-Link */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Also Serving Abu Dhabi & the UAE
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Looking for technology services in Abu Dhabi? We provide cloud infrastructure, DevOps, cybersecurity, and AI solutions for the UAE capital.
            </p>
            <Link
              to="/abu-dhabi-services"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
            >
              View Abu Dhabi Services
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Latest Insights Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Latest Insights for Dubai Businesses
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Explore our latest articles on software development, digital transformation, and technology trends in the UAE market.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {blogPosts.map((post, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                >
                  <Link
                    to={post.path}
                    className="group block bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-500 h-full"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex-shrink-0" aria-hidden="true">
                        <post.icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">
                          {post.description}
                        </p>
                        <span className="inline-flex items-center mt-3 text-primary-600 dark:text-primary-400 text-sm font-medium group-hover:underline">
                          Read more
                          <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Start Your Project in Dubai?"
        description="Let's discuss how we can help your business succeed in the UAE market. Schedule a free consultation with our Dubai-focused team and discover the right technology solutions for your specific needs."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact', asAnchor: true }}
        footnote="Business Bay, Dubai, UAE | Available Sunday - Thursday, 9:00 AM - 6:00 PM GST"
      />
    </div>
  )
}

export default DubaiServices
